import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Button from '../components/ui/Button';
import TKFIcon from '../components/icons/TKFIcon';
import TypingAnimation from '../components/ui/TypingAnimation';
import { Container, Section, SectionLabel, Lede } from '../components/ui/Section';
import { useSiteContent } from '../hooks/useSiteContent';

gsap.registerPlugin(ScrollTrigger);

// Hero Section
const HeroSection = styled.section`
  position: relative;
  /* The sticky header is in flow, so subtract its height or the hero overruns. */
  min-height: calc(100vh - 4rem);
  min-height: calc(100dvh - 4rem);
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  color: ${props => props.theme.colors.white};
  padding: ${props => props.theme.spacing[20]} 0 ${props => props.theme.spacing[8]};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    min-height: calc(100vh - 4.5rem);
    min-height: calc(100dvh - 4.5rem);
  }
`;

const HeroVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    display: none;
    will-change: auto;
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    object-position: center;
    will-change: auto;
  }
`;

/**
 * Two stacked scrims: a soft top wash so the sticky header stays legible, and a
 * heavier bottom gradient under the copy. Flat black overlays kill the footage.
 */
const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    linear-gradient(180deg, rgba(16, 18, 16, 0.55) 0%, rgba(16, 18, 16, 0) 32%),
    linear-gradient(0deg, rgba(16, 18, 16, 0.88) 0%, rgba(16, 18, 16, 0.35) 45%, rgba(16, 18, 16, 0.1) 100%);

  @media (prefers-reduced-motion: reduce) {
    background: ${props => props.theme.colors.primary[900]};
  }
`;

const HeroInner = styled(Container)`
  position: relative;
  z-index: 2;
`;

const HeroEyebrow = styled(motion.div)`
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  margin-bottom: ${props => props.theme.spacing[6]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${props => props.theme.borderRadius.full};
    background: ${props => props.theme.colors.primary[400]};
  }
`;

const HeroTitle = styled(motion.h1)`
  max-width: 16ch;
  margin-bottom: ${props => props.theme.spacing[6]};
  color: ${props => props.theme.colors.white};

  .typing-line {
    display: block;
  }

  .typing-text {
    color: ${props => props.theme.colors.primary[300]};
  }
`;

/** Quiet secondary action for use on the hero footage. */
const HeroTextLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: rgba(255, 255, 255, 0.82);
  padding-bottom: 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  transition: color ${props => props.theme.animation.duration.fast} ease,
    border-color ${props => props.theme.animation.duration.fast} ease;

  &:hover {
    color: ${props => props.theme.colors.white};
    border-bottom-color: ${props => props.theme.colors.white};
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: ${props => props.theme.typography.fontSize.lg};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
  color: rgba(255, 255, 255, 0.78);
  max-width: 46ch;
  margin-bottom: ${props => props.theme.spacing[10]};
`;

const HeroActions = styled(motion.div)`
  display: flex;
  gap: ${props => props.theme.spacing[4]};
  flex-direction: column;
  align-items: flex-start;

  @media (min-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: row;
    align-items: center;
  }
`;

/** Hairline foot of the hero: a quiet cue that the page continues. */
const HeroFooter = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[12]};
  padding-top: ${props => props.theme.spacing[4]};
  border-top: 1px solid rgba(255, 255, 255, 0.18);
  font-size: ${props => props.theme.typography.fontSize.xs};
  letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);

  svg {
    width: 14px;
    height: 14px;
  }
`;

/**
 * Section header: heading on the left, supporting copy on the right. The
 * asymmetry is what keeps a minimal page from reading as a stack of boxes.
 */
const SplitHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing[6]};
  align-items: end;
  margin-bottom: ${props => props.theme.spacing[12]};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1.1fr 1fr;
    gap: ${props => props.theme.spacing[16]};
  }
`;

const SplitTitle = styled.h2`
  max-width: 14ch;
  margin: 0;
`;

const InitiativeCard = styled.article`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing[8]};
  padding: ${props => props.theme.spacing[8]};
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius['2xl']};

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr auto;
    align-items: center;
    padding: ${props => props.theme.spacing[12]};
    gap: ${props => props.theme.spacing[16]};
  }

  h3 {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    margin-bottom: ${props => props.theme.spacing[4]};
  }

  p {
    color: ${props => props.theme.colors.textMuted};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};
    max-width: 54ch;
    margin-bottom: ${props => props.theme.spacing[8]};
  }
`;

/** The one place a large green number is allowed — the campaign goal. */
const GoalFigure = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  padding-top: ${props => props.theme.spacing[6]};
  border-top: 1px solid ${props => props.theme.colors.border};

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding-top: 0;
    padding-left: ${props => props.theme.spacing[12]};
    border-top: none;
    border-left: 1px solid ${props => props.theme.colors.border};
    min-width: 240px;
  }

  .amount {
    font-family: ${props => props.theme.typography.fontFamily.heading};
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    letter-spacing: ${props => props.theme.typography.letterSpacing.display};
    color: ${props => props.theme.colors.primary[500]};
    line-height: 1;
  }

  .caption {
    font-size: ${props => props.theme.typography.fontSize.xs};
    letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
    text-transform: uppercase;
    color: ${props => props.theme.colors.textMuted};
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing[8]};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    gap: ${props => props.theme.spacing[16]};
  }

  p {
    color: ${props => props.theme.colors.textMuted};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};

    & + p {
      margin-top: ${props => props.theme.spacing[5]};
    }
  }
`;

const QuoteBlock = styled.div`
  max-width: 780px;
  margin: 0 auto;
  text-align: center;

  .mark {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: ${props => props.theme.spacing[10]};
    opacity: 0.9;
  }

  blockquote {
    font-family: ${props => props.theme.typography.fontFamily.heading};
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    font-weight: ${props => props.theme.typography.fontWeight.normal};
    letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
    line-height: ${props => props.theme.typography.lineHeight.snug};
    color: ${props => props.theme.colors.ink};
    margin-bottom: ${props => props.theme.spacing[8]};
    text-wrap: balance;
  }

  .attribution {
    font-size: ${props => props.theme.typography.fontSize.xs};
    letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
    text-transform: uppercase;
    color: ${props => props.theme.colors.textMuted};
  }
`;

const CenteredEmpty = styled.div`
  max-width: 640px;

  p {
    color: ${props => props.theme.colors.textMuted};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};
    margin-bottom: ${props => props.theme.spacing[8]};
  }
`;

const Home: React.FC = () => {
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  const { siteContent, isLoading } = useSiteContent();

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.innerWidth < 768;

    if (prefersReducedMotion) return;

    // Sections rise into place — short travel, no fade-out on scroll back up.
    sectionsRef.current.forEach((section) => {
      if (!section) return;
      gsap.fromTo(section,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 85%',
            toggleActions: 'play none none none'
          }
        }
      );
    });

    // Slow push on the hero footage as the page scrolls away from it.
    if (!isMobile && heroVideoRef.current) {
      gsap.to(heroVideoRef.current, {
        scale: 1.08,
        ease: 'none',
        scrollTrigger: {
          trigger: heroVideoRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1
        }
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const currentInitiative = siteContent?.initiatives?.current?.[0];
  const activeCount = siteContent?.initiatives?.current?.length ?? 0;

  return (
    <div>
      {/* Hero Section */}
      <HeroSection>
        <HeroVideo
          ref={heroVideoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label="Background video showing TKF Foundation's volunteer activities"
        >
          <source src="/hero_background.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </HeroVideo>
        <HeroOverlay />
        <HeroInner>
          <HeroEyebrow
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            The Kuddy Foundation
          </HeroEyebrow>
          <HeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            {siteContent?.hero.title.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < siteContent.hero.title.length - 1 && <br />}
              </React.Fragment>
            )) || (
              <>
                One Finger. <br />
                One Moment. <br />
              </>
            )}
            <span className="typing-line">
              <TypingAnimation
                texts={siteContent?.hero.typingTexts || ["Start change.", "Make impact.", "Begin today."]}
                speed={150}
                deleteSpeed={100}
                pauseDuration={2500}
                infinite={true}
                className="typing-text"
              />
            </span>
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {siteContent?.hero.subtitle || "Join us Today"}
          </HeroSubtitle>
          <HeroActions
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {siteContent?.donationButton.enabled && (
              <Button
                as="a"
                variant="accent"
                size="lg"
                icon={<ArrowRight />}
                href={siteContent.donationButton.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {isLoading ? "Donate Now" : siteContent.donationButton.text}
              </Button>
            )}
            <HeroTextLink to="/initiatives">See our work</HeroTextLink>
          </HeroActions>
          <HeroFooter>
            <ArrowDown aria-hidden="true" />
            <span>Scroll</span>
          </HeroFooter>
        </HeroInner>
      </HeroSection>

      {/* Current Initiative */}
      <Section ref={el => { sectionsRef.current[0] = el; }} $size="lg">
        <Container>
          <SectionLabel>{activeCount > 1 ? 'Current Initiatives' : 'Current Initiative'}</SectionLabel>
          {currentInitiative ? (
            <>
              <SplitHeader>
                <SplitTitle>Where your support goes right now</SplitTitle>
                <Lede>
                  Every campaign we run is youth-led, single-purpose, and accounted for in
                  the open — one initiative at a time, until it is finished.
                </Lede>
              </SplitHeader>

              <InitiativeCard>
                <div>
                  <h3>{currentInitiative.title}</h3>
                  <p>{currentInitiative.shortDescription}</p>
                  <Button
                    as={Link}
                    to={`/initiatives/${currentInitiative.slug}`}
                    variant="primary"
                    icon={<ArrowRight />}
                  >
                    Learn more
                  </Button>
                </div>
                <GoalFigure>
                  <span className="amount">
                    ${currentInitiative.fallbackGoal?.toLocaleString() || "10,000"}
                  </span>
                  <span className="caption">Campaign goal</span>
                </GoalFigure>
              </InitiativeCard>

              {activeCount > 1 && (
                <div style={{ marginTop: '2rem' }}>
                  <Button as={Link} to="/initiatives" variant="secondary">
                    View all {activeCount} active campaigns
                  </Button>
                </div>
              )}
            </>
          ) : (
            <SplitHeader>
              <SplitTitle>Our impact continues</SplitTitle>
              <CenteredEmpty>
                <p>
                  We're currently planning our next initiative to create meaningful change in
                  our community. Stay tuned for updates on how you can get involved.
                </p>
                <Button as={Link} to="/initiatives" variant="primary" icon={<ArrowRight />}>
                  View our past work
                </Button>
              </CenteredEmpty>
            </SplitHeader>
          )}
        </Container>
      </Section>

      {/* About */}
      <Section ref={el => { sectionsRef.current[1] = el; }} $surface="muted" $size="lg">
        <Container>
          <SectionLabel>Who we are</SectionLabel>
          <AboutGrid>
            <SplitTitle>A Brampton foundation, built by the people it serves</SplitTitle>
            <div>
              {siteContent?.aboutUs.sections.map((section, index) => (
                <p key={index}>{section.content}</p>
              )) || (
                <p>
                  The Kuddy Foundation is a Brampton based non-profit that supports families
                  facing financial and healthcare challenges.
                </p>
              )}
            </div>
          </AboutGrid>
        </Container>
      </Section>

      {/* President Quote */}
      {siteContent?.team && siteContent.team.length > 0 && (
        <Section ref={el => { sectionsRef.current[2] = el; }} $size="lg">
          <Container>
            <QuoteBlock>
              <div className="mark">
                <TKFIcon size={140} />
              </div>
              <blockquote>"{siteContent.team[0].quote}"</blockquote>
              <div className="attribution">
                {siteContent.team[0].name} — {siteContent.team[0].role}
              </div>
            </QuoteBlock>
          </Container>
        </Section>
      )}
    </div>
  );
};

export default Home;
